import * as Yup from 'yup';
import { useFormik } from 'formik';

// Common
import { Button, FormControlLabel, Grid, Paper, Switch } from '@mui/material';
import TextField from '@mui/material/TextField';

// Hooks and services
import { updateAppUser } from '../../../services/user.service';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

// Types
import { AppUser } from '../../../types/AppUser';

export interface AppUserInput {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  location?: string;
  isDisabled: boolean;
}

interface appUserFormProps {
  appUser: AppUser | undefined;
  setAppUser: (appUser: AppUser) => void;
}

export default function AppUserForm(props: appUserFormProps) {
  const { notifyMessage } = useNotifyMessage();
  const initialValue: AppUserInput = {
    firstname: props.appUser?.firstName ?? '',
    lastname: props.appUser?.lastName ?? '',
    email: props.appUser?.email ?? '',
    phone: props.appUser?.phone ?? '',
    location: props.appUser?.location,
    isDisabled: props.appUser?.isDisabled ?? false,
  };

  const validationSchema = () => {
    return Yup.object<AppUserInput>({
      firstname: Yup.string().required('Firstname is required'),
      lastname: Yup.string().required('Lastname is required'),
      email: Yup.string().required('Email is required').email('Email must be a valid email address'),
      location: Yup.string().length(3, 'Must be exactly 3 characters').nullable(),
    });
  };
  const handleSubmit = async (values) => {
    if (!props.appUser) {
      console.error('No current appUser for update');
      return;
    }

    try {
      const response = await updateAppUser(props.appUser.id, values);
      props.setAppUser(response);
      notifyMessage('User updated', 'success');
    } catch {
      notifyMessage('Update failed', 'error');
    }
  };

  const formik = useFormik({
    initialValues: initialValue,
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  return (
    <Paper elevation={3} style={{ overflowX: 'auto' }}>
      <form onSubmit={formik.handleSubmit} style={{ width: 'auto', height: 'auto', padding: 20 }}>
        <Grid container spacing={3}>
          <Grid xs={12} item>
            <TextField
              id='firstname'
              name='firstname'
              label='Firstname'
              value={formik.values?.firstname ?? ''}
              onChange={formik.handleChange}
              error={formik.touched.firstname && Boolean(formik.errors.firstname)}
              helperText={formik.touched.firstname && formik.errors.firstname}
              placeholder='Firstname'
              variant='standard'
              size='small'
              fullWidth
              required
              margin='dense'
              style={{ width: 400 }}
            />
          </Grid>
          <Grid xs={12} item>
            <TextField
              id='lastname'
              name='lastname'
              label='Lastname'
              value={formik.values?.lastname ?? ''}
              onChange={formik.handleChange}
              error={formik.touched.lastname && Boolean(formik.errors.lastname)}
              helperText={formik.touched.lastname && formik.errors.lastname}
              placeholder='lastname'
              variant='standard'
              size='small'
              fullWidth
              required
              margin='dense'
              style={{ width: 400 }}
            />
          </Grid>

          <Grid xs={12} item>
            <TextField
              id='email'
              name='email'
              label='Email'
              value={formik.values?.email ?? ''}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              placeholder='email'
              variant='standard'
              size='small'
              fullWidth
              required
              margin='dense'
              style={{ width: 400 }}
            />
          </Grid>

          <Grid xs={12} item>
            <TextField
              id='phone'
              name='phone'
              label='Phone'
              value={formik.values?.phone ?? ''}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
              placeholder='phone'
              variant='standard'
              size='small'
              fullWidth
              required
              margin='dense'
              style={{ width: 400 }}
            />
          </Grid>

          <Grid xs={12} item>
            <TextField
              id='location'
              name='location'
              label='Airport (IATA)'
              value={formik.values?.location ?? ''}
              onChange={formik.handleChange}
              error={formik.touched.location && Boolean(formik.errors.location)}
              helperText={formik.touched.location && formik.errors.location}
              placeholder='IATA station code (e.g. AMS for Schiphol)'
              variant='standard'
              size='small'
              fullWidth
              margin='dense'
              style={{ width: 400 }}
            />
          </Grid>

          <Grid xs={12} item>
            <FormControlLabel
              control={
                <Switch
                  id='isDisabled'
                  name='isDisabled'
                  checked={!formik.values?.isDisabled}
                  onChange={(e) => {
                    formik.setFieldValue('isDisabled', !e.target.checked);
                  }}
                />
              }
              label='Active'
            />
          </Grid>
          <Grid item>
            <Button type='submit' color='primary' variant='contained' size='small' sx={tablePrimaryButtonSx}>
              Save
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
}
